// src/services/jwt.ts
import { reportError, reportMessage } from "@/utils/sentry";
import { cookieService } from "./cookieService";
import { jwtLifecycleService, JWT_CONFIG } from "./jwtLifecycleService";

export interface JWTPayload {
  tokenType: string;
  username: string;
  name: string;
  grade: string;
  major_class: string;
  id: number;
  role: string;
  is_superuser: boolean;
  is_staff: boolean;
  isEmail: boolean;
  exp: number;
  iat?: number;
  [key: string]: unknown;
}

export interface TokenResponse {
  access: string;
  refresh: string;
  expires_in?: number;
}

/** JWT 服务 */
export class JWTService {
  private tokenVerifyPromise: Promise<boolean> | null = null;
  private lastValidationTime = 0;
  private readonly VALIDATION_CACHE_MS = 30000; // 30秒内不重复验证

  constructor() {}

  /** 检查是否应该禁用验证 */
  public shouldDisableVerification(): boolean {
    const currentProtocol = window.location.protocol;
    const apiUrl = new URL(import.meta.env.VITE_API_BASE_URL);
    const shouldDisable =
      (currentProtocol === "https:" && apiUrl.protocol === "http:") ||
      (currentProtocol === "http:" && apiUrl.protocol === "https:");

    if (shouldDisable) {
      console.log("🚫 Verification disabled due to protocol mismatch:", {
        currentProtocol,
        apiProtocol: apiUrl.protocol,
        apiBaseUrl: import.meta.env.VITE_API_BASE_URL,
      });
    }

    return shouldDisable;
  }

  /** 解析 JWT payload */
  private parseJWTPayload(token: string): JWTPayload | null {
    try {
      const base64Payload = token.split(".")[1];
      const normalizedPayload = base64Payload
        .replace(/-/g, "+")
        .replace(/_/g, "/");
      const padding = "=".repeat((4 - (normalizedPayload.length % 4)) % 4);
      const decodedPayload = decodeURIComponent(
        Array.prototype.map
          .call(
            atob(normalizedPayload + padding),
            (c: string) =>
              "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2),
          )
          .join(""),
      );
      return JSON.parse(decodedPayload);
    } catch (error) {
      console.error("Failed to parse JWT payload:", error);
      reportError(error as Error, {
        type: "jwt_parse_error",
        action: "parse_payload",
      });
      return null;
    }
  }

  /** 解析 token */
  public parseToken(token: string): JWTPayload | null {
    return this.parseJWTPayload(token);
  }

  /** 获取 token 过期时间 */
  private getTokenExpiration(token: string): number {
    const payload = this.parseJWTPayload(token);
    return payload?.exp ? payload.exp * 1000 : 0; // 转换为毫秒
  }

  /** 验证 refresh token */
  private validateRefreshToken(): string | null {
    const refreshToken = cookieService.get("refresh_token");
    if (!refreshToken) {
      return null;
    }

    try {
      const expiration = this.getTokenExpiration(refreshToken);
      if (expiration > Date.now()) {
        return refreshToken;
      } else {
        console.warn("Refresh token has expired");
        cookieService.remove("refresh_token");
        return null;
      }
    } catch (error) {
      console.error("Failed to validate refresh token:", error);
      cookieService.remove("refresh_token");
      return null;
    }
  }

  /** 获取 access token */
  public getToken(): string | null {
    return cookieService.get("jwt_token");
  }

  /** 获取 refresh token */
  public getRefreshToken(): string | null {
    return cookieService.get("refresh_token");
  }

  /** 本地检查 token 是否过期 */
  public isTokenExpiredLocally(token: string | null): boolean {
    if (!token) return true;

    try {
      const payload = this.parseJWTPayload(token);
      if (!payload?.exp) return true;

      // 添加30秒的缓冲时间，避免边界情况
      return payload.exp * 1000 < Date.now() + 30000;
    } catch (error) {
      console.error("Failed to check token expiration locally:", error);
      return true;
    }
  }

  /** 智能验证令牌有效性 */
  private async verifyToken(token: string): Promise<boolean> {
    const callId = Math.random().toString(36).substring(2, 6);
    console.log(`🔐 verifyToken called [${callId}]`);

    // 检查缓存，避免短时间内重复验证
    // 但如果从未验证过（lastValidationTime = 0），则必须进行验证
    const now = Date.now();
    if (
      this.lastValidationTime > 0 &&
      now - this.lastValidationTime < this.VALIDATION_CACHE_MS
    ) {
      console.log(`🔐 Token validation skipped due to cache [${callId}]`);
      return true; // 假设最近验证过的token仍然有效
    }

    // 如果本地检测已过期，直接返回 false
    if (this.isTokenExpiredLocally(token)) {
      console.log(
        `🔐 Token expired locally, skipping verification API call [${callId}]`,
      );
      return false;
    }

    // 检查协议匹配
    if (this.shouldDisableVerification()) {
      console.log(
        `🔐 Verification disabled due to protocol mismatch [${callId}]`,
      );
      return true; // 协议不匹配时假设token有效
    }

    try {
      console.log(
        `🔐 Making server verification request to /token_verify/ [${callId}]`,
      );

      // 避免动态导入阻塞，直接使用fetch进行验证请求
      const apiBaseURL = import.meta.env.DEV
        ? window.location.origin + "/api"
        : import.meta.env.VITE_API_BASE_URL;

      console.log(`🔐 Using API base URL: ${apiBaseURL} [${callId}]`);

      const response = await fetch(`${apiBaseURL}/token_verify/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token }),
      });

      console.log(
        `🔐 Server verification response received [${callId}]:`,
        response.status,
      );

      if (response.ok) {
        this.lastValidationTime = now;
        return true;
      } else {
        console.error(
          `🔐 Token verification failed with status ${response.status} [${callId}]`,
        );
        return false;
      }
    } catch (error) {
      console.error(`🔐 Token verification failed [${callId}]:`, error);
      reportError(error as Error, {
        type: "auth_error",
        action: "verify_token",
        callId,
      });
      return false;
    }
  }

  /** 刷新令牌 */
  private async refreshToken(
    refreshToken: string,
  ): Promise<TokenResponse | null> {
    try {
      // 避免动态导入阻塞，直接使用fetch进行刷新请求
      const apiBaseURL = import.meta.env.DEV
        ? window.location.origin + "/api"
        : import.meta.env.VITE_API_BASE_URL;

      const response = await fetch(`${apiBaseURL}/token_refresh/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refresh: refreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data?.access) {
          reportMessage("令牌刷新成功", "info");
          return data;
        }
      }
    } catch (error) {
      console.error("Failed to refresh token:", error);
      reportError(error as Error, {
        type: "auth_error",
        action: "refresh_token",
      });
    }
    return null;
  }

  /** 检查是否有有效的 tokens */
  public hasValidTokens(): boolean {
    const jwtToken = this.getToken();
    const refreshToken = this.getRefreshToken();

    // 至少需要有一个token
    if (!jwtToken && !refreshToken) {
      return false;
    }

    // 如果有JWT token，检查是否在本地未过期
    if (jwtToken && !this.isTokenExpiredLocally(jwtToken)) {
      return true;
    }

    // 如果JWT token过期但有refresh token，也认为有效
    if (refreshToken) {
      try {
        const expiration = this.getTokenExpiration(refreshToken);
        return expiration > Date.now();
      } catch {
        return false;
      }
    }

    return false;
  }

  /** 清除验证 Promise */
  public clearVerificationPromise(): void {
    this.tokenVerifyPromise = null;
    this.lastValidationTime = 0;
    console.log("🧹 Verification promise and cache cleared manually");
  }

  /** 设置 tokens */
  public async setTokens(access: string, refresh: string): Promise<void>;
  public async setTokens(tokens: TokenResponse): Promise<void>;
  public async setTokens(
    accessOrTokens: string | TokenResponse,
    refresh?: string,
  ): Promise<void> {
    let access: string;
    let refreshToken: string;

    // 支持两种调用方式：setTokens(access, refresh) 或 setTokens({access, refresh})
    if (typeof accessOrTokens === "string") {
      access = accessOrTokens;
      refreshToken = refresh || "";
    } else {
      access = accessOrTokens.access;
      refreshToken = accessOrTokens.refresh;
    }

    if (!access) {
      console.error("Invalid tokens: missing access token");
      return;
    }

    if (!refreshToken) {
      console.error("Invalid tokens: missing refresh token");
      return;
    }

    // 验证access token格式
    const payload = this.parseJWTPayload(access);
    if (!payload?.exp) {
      console.error("Invalid JWT payload: missing exp field");
      return;
    }

    try {
      // 设置access token（5天过期）
      cookieService.set("jwt_token", access, {
        expires: JWT_CONFIG.ACCESS_TOKEN_DAYS,
      });

      // 设置refresh token（7天过期）
      cookieService.set("refresh_token", refreshToken, {
        expires: JWT_CONFIG.REFRESH_TOKEN_DAYS,
      });

      // 清除之前的验证状态，确保下次验证时重新开始
      this.tokenVerifyPromise = null;
      this.lastValidationTime = 0;
      console.log("🧹 Verification state cleared after setting new tokens");

      // 启动JWT生命周期管理
      jwtLifecycleService.start();

      console.log("Tokens set successfully", {
        hasAccess: !!access,
        hasRefresh: !!refreshToken,
        accessExpires: new Date(payload.exp * 1000).toISOString(),
      });

      reportMessage("JWT tokens设置成功", "info");
    } catch (error) {
      console.error("Failed to set tokens:", error);
      reportError(error as Error, {
        type: "jwt_error",
        action: "set_tokens",
      });
      throw error;
    }
  }

  /** 确保令牌有效 */
  public async ensureValidTokens(): Promise<boolean> {
    const callId = Math.random().toString(36).substring(2, 8);
    const caller = new Error().stack?.split("\n")[2]?.trim() || "unknown";
    console.log(`🔍 ensureValidTokens called [${callId}] from: ${caller}`);

    // 首先进行Cookie路径迁移检查（托底机制）
    try {
      cookieService.migrateCookiesToRootPath();
      console.log(`🔧 Cookie path migration completed [${callId}]`);
    } catch (error) {
      console.warn(`⚠️ Cookie path migration failed [${callId}]:`, error);
    }

    // 防止并发调用，但允许第一次调用的日志正常显示
    if (this.tokenVerifyPromise) {
      console.log(`⚠️ Using existing verification promise [${callId}]`);
      const result = await this.tokenVerifyPromise;
      console.log(`📝 Existing promise result: ${result} [${callId}]`);
      return result;
    }

    this.tokenVerifyPromise = (async () => {
      console.log(
        `🔧 Creating verification promise [${callId}] - Caller: ${caller.substring(caller.lastIndexOf("/") + 1)}`,
      );
      try {
        const jwtToken = this.getToken();
        const refreshToken = this.validateRefreshToken();

        console.log(`🔍 Token availability [${callId}]:`, {
          hasJwtToken: !!jwtToken,
          hasRefreshToken: !!refreshToken,
          jwtTokenLength: jwtToken?.length,
          refreshTokenLength: refreshToken?.length,
        });

        // 没有任何token
        if (!jwtToken && !refreshToken) {
          console.log(`❌ 没有可用的令牌 [${callId}]`);
          return false;
        }

        // 只有refresh token，尝试获取新的access token
        if (!jwtToken && refreshToken) {
          console.log(`🔄 缺少JWT令牌，尝试刷新 [${callId}]`);
          const tokens = await this.refreshToken(refreshToken);
          if (tokens) {
            await this.setTokens(tokens.access, tokens.refresh);
            console.log(`✅ Token refresh successful [${callId}]`);
            return true;
          }
          console.log(`❌ Token refresh failed [${callId}]`);
          this.clearTokens();
          return false;
        }

        // 有access token，检查有效性
        if (jwtToken) {
          console.log(`🔍 Checking JWT token validity [${callId}]`);

          // 本地检查过期
          const isExpired = this.isTokenExpiredLocally(jwtToken);
          console.log(`🔍 Token expiry check [${callId}]:`, { isExpired });

          if (isExpired) {
            console.log(`⏰ JWT令牌本地检测已过期 [${callId}]`);
            if (!refreshToken) {
              console.log(
                `❌ No refresh token available for renewal [${callId}]`,
              );
              this.clearTokens();
              return false;
            }

            // 尝试刷新
            console.log(`🔄 尝试使用refresh token刷新 [${callId}]`);
            const tokens = await this.refreshToken(refreshToken);
            if (tokens) {
              await this.setTokens(tokens.access, tokens.refresh);
              console.log(
                `✅ Token refresh after expiry successful [${callId}]`,
              );
              return true;
            }
            console.log(`❌ Token refresh after expiry failed [${callId}]`);
            this.clearTokens();
            return false;
          }

          // 本地检查通过，进行服务端验证
          console.log(
            `🌐 Local validation passed, calling server verification API [${callId}]`,
          );
          console.log(`🌐 Token for verification [${callId}]:`, {
            tokenLength: jwtToken.length,
            tokenPrefix: jwtToken.substring(0, 20) + "...",
          });

          const isValid = await this.verifyToken(jwtToken);
          console.log(`🌐 Server verification result [${callId}]:`, {
            isValid,
            timestamp: new Date().toISOString(),
          });

          if (!isValid) {
            console.log(`❌ Server verification failed [${callId}]`);
            // 服务端验证失败，尝试刷新token
            if (refreshToken) {
              console.log(
                `🔄 Attempting refresh after server verification failure [${callId}]`,
              );
              const tokens = await this.refreshToken(refreshToken);
              if (tokens) {
                await this.setTokens(tokens.access, tokens.refresh);
                console.log(
                  `✅ Token refresh after verification failure successful [${callId}]`,
                );
                return true;
              }
              console.log(
                `❌ Token refresh after verification failure failed [${callId}]`,
              );
            }
            this.clearTokens();
            return false;
          }

          // Token有效
          console.log(`✅ JWT令牌验证通过 [${callId}]`);
          return true;
        }

        console.log(`❌ Unexpected condition reached [${callId}]`);
        return false;
      } catch (error) {
        console.error(`💥 Token validation error [${callId}]:`, error);
        return false;
      } finally {
        // 重要：清理promise引用
        this.tokenVerifyPromise = null;
        console.log(`🧹 Token verification promise cleared [${callId}]`);
      }
    })();

    const result = await this.tokenVerifyPromise;
    console.log(`📋 Final verification result: ${result} [${callId}]`);
    return result;
  }

  /** 清除所有 tokens */
  public clearTokens(): void {
    try {
      // 停止JWT生命周期管理
      jwtLifecycleService.stop();

      // 使用增强的Cookie清理方法
      cookieService.clearAllAccessiblePaths();

      // 清除其他可能存在的相关cookie
      cookieService.remove("access_token");
      cookieService.remove("token");
      cookieService.remove("auth_token");

      // 清除localStorage和sessionStorage中的相关数据
      const storageKeys = [
        "jwt_token",
        "refresh_token",
        "auth_token",
        "token",
        "access_token",
        "user_info",
        "auth_state",
      ];

      storageKeys.forEach((key) => {
        try {
          localStorage.removeItem(key);
          sessionStorage.removeItem(key);
        } catch (e) {
          console.debug(`Failed to remove ${key} from storage:`, e);
        }
      });

      // 重置内部状态
      this.tokenVerifyPromise = null;
      this.lastValidationTime = 0;

      console.log("All tokens cleared successfully from all accessible paths");
      reportMessage("JWT tokens已清除", "info");
    } catch (error) {
      console.error("Failed to clear tokens:", error);
      reportError(error as Error, {
        type: "jwt_error",
        action: "clear_tokens",
      });
    }
  }

  /** 获取当前 JWT 服务状态 */
  public getStatus(): {
    hasAccessToken: boolean;
    hasRefreshToken: boolean;
    isValidating: boolean;
    lastValidationTime: number;
    lifecycleStatus: unknown;
  } {
    return {
      hasAccessToken: !!this.getToken(),
      hasRefreshToken: !!this.getRefreshToken(),
      isValidating: !!this.tokenVerifyPromise,
      lastValidationTime: this.lastValidationTime,
      lifecycleStatus: jwtLifecycleService.getStatus(),
    };
  }
}

export const jwtService = new JWTService();
